/**
 * MergeOrFinalizeDialog — shown when POST /meetings/:id/finalize returns a
 * 409 with fuzzy duplicate candidates. Presents the user with three paths:
 *
 *   1. Merge into existing — folds this draft's topics / action items /
 *      attendees into the selected finalized meeting via
 *      POST /meetings/:id/merge-into. The draft is soft-deleted; the user
 *      is navigated to the updated finalized record.
 *
 *   2. Finalize as separate record — runs the original force=1 finalize.
 *
 *   3. Cancel — closes the dialog; user goes back to edit title / date.
 *
 * This replaces the earlier window.confirm() path so the list of matches
 * is clearly visible and the merge option is first-class.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/apiClient.ts';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/Modal.tsx';

export interface DuplicateCandidate {
  id: string;
  title: string;
  meetingDate: string;
  projectId: string;
  finalizedAt: string | null;
  ownerUserId: string;
  similarity: number;
}

interface Props {
  meetingId: string;
  /** Array from POST /meetings/:id/finalize 409 response, ranked. */
  duplicates: DuplicateCandidate[];
  isOpen: boolean;
  onClose: () => void;
  /** Called after Merge succeeds — parent navigates to the target meeting. */
  onMerged: (targetId: string) => void;
  /** Called after Finalize-as-separate succeeds. */
  onFinalized: () => void;
}

export function MergeOrFinalizeDialog({
  meetingId,
  duplicates,
  isOpen,
  onClose,
  onMerged,
  onFinalized,
}: Props) {
  // Default pick: highest-similarity candidate.
  const [selectedId, setSelectedId] = useState<string>(
    duplicates[0]?.id ?? '',
  );

  const merge = useMutation({
    mutationFn: (targetMeetingId: string) =>
      api.post<{ target: { id: string }; stats: MergeStats }>(
        `/meetings/${meetingId}/merge-into`,
        { targetMeetingId },
      ),
    onSuccess: (data) => {
      onMerged(data.target.id);
    },
  });

  const finalize = useMutation({
    mutationFn: () => api.post(`/meetings/${meetingId}/finalize?force=1`),
    onSuccess: () => {
      onFinalized();
    },
  });

  if (!isOpen) return null;

  const errMsg =
    merge.error instanceof ApiError
      ? merge.error.message
      : merge.error instanceof Error
        ? merge.error.message
        : finalize.error instanceof ApiError
          ? finalize.error.message
          : finalize.error instanceof Error
            ? finalize.error.message
            : null;
  const busy = merge.isPending || finalize.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      ariaLabel="Similar meeting already finalized"
    >
      <ModalHeader
        title="Similar meeting already finalized"
        subtitle="A finalized meeting with a similar title exists on the same date for this project. Pick one and choose how to proceed."
        onClose={onClose}
      />

      <ModalBody>
        <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-pine">
          Existing finalized meetings
        </div>
        <ul
          className="overflow-hidden rounded-2xl border border-charcoal/10 bg-paper-light"
          role="radiogroup"
          aria-label="Existing finalized candidates"
        >
          {duplicates.map((d, i) => (
            <li
              key={d.id}
              className={i > 0 ? 'border-t border-charcoal/10' : ''}
            >
              <label
                className={
                  'flex cursor-pointer items-start gap-3 px-4 py-2.5 transition-colors ' +
                  (selectedId === d.id ? 'bg-pine/5' : 'hover:bg-pine/5')
                }
              >
                <input
                  type="radio"
                  name="dup-candidate"
                  value={d.id}
                  checked={selectedId === d.id}
                  onChange={() => setSelectedId(d.id)}
                  className="mt-1 accent-pine"
                />
                <span className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-charcoal">
                    {d.title}
                  </div>
                  <div className="mt-0.5 text-[11px] text-charcoal/60">
                    Meeting {d.meetingDate}
                    {d.finalizedAt && (
                      <> · finalized {new Date(d.finalizedAt).toLocaleDateString()}</>
                    )}
                    {' · '}
                    <span className="font-semibold text-pine">
                      {Math.round(d.similarity * 100)}% title match
                    </span>
                  </div>
                </span>
              </label>
            </li>
          ))}
        </ul>

        <div className="mt-5 space-y-2 text-xs text-charcoal/75">
          <p>
            <b>Merge into existing</b> appends this draft's topics, action
            items, and attendees onto the selected meeting. The draft is
            then soft-deleted.
          </p>
          <p>
            <b>Finalize as a separate record</b> keeps both meetings (useful
            when the names coincidentally overlap but the meetings are
            genuinely different).
          </p>
        </div>

        {errMsg && (
          <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {errMsg}
          </div>
        )}
      </ModalBody>

      <ModalFooter className="flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => finalize.mutate()}
          disabled={busy}
          className="rounded-full border border-pine bg-white px-4 py-2 text-sm font-semibold text-pine transition-colors hover:bg-pine/5 disabled:opacity-50"
        >
          {finalize.isPending ? 'Finalizing…' : 'Finalize as separate record'}
        </button>
        <button
          type="button"
          onClick={() => selectedId && merge.mutate(selectedId)}
          disabled={busy || !selectedId}
          className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90 disabled:opacity-50"
        >
          {merge.isPending ? 'Merging…' : 'Merge into selected'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

interface MergeStats {
  topicsAdded: number;
  actionItemsAdded: number;
  attendeeGroupsAddedOrMerged: number;
  attendeeMembersAdded: number;
}
